"use client"

import { useEffect, useState } from "react"
import Modal from "antd/es/modal/Modal"
import { getFichaMedica } from "../services/fichasMedService"
import Swal from "sweetalert2"
import jsPDF from "jspdf"

function Field({ label, value, onChange, type = "text", step, required = false, placeholder, disabled = false, help }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-700 mb-1">
        {label} {required && <span className="text-rose-600">*</span>}
      </span>
      <input
        type={type}
        step={step}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
        required={required}
      />
      {help && <span className="block text-xs text-gray-400 mt-1">{help}</span>}
    </label>
  )
}

function TextAreaField({ label, value, onChange, required = false, placeholder, disabled = false, rows = 2, help }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-700 mb-1">
        {label} {required && <span className="text-rose-600">*</span>}
      </span>
      <textarea
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        rows={rows}
        className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed resize-none"
        required={required}
      />
      {help && <span className="block text-xs text-gray-400 mt-1">{help}</span>}
    </label>
  )
}

function SelectField({ label, value, onChange, options, required = false, placeholder, disabled = false, help }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-700 mb-1">
        {label} {required && <span className="text-rose-600">*</span>}
      </span>
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
        required={required}
      >
        <option value="">{placeholder || "Selecciona una opción"}</option>
        {options.map((option, idx) => (
          <option key={idx} value={option}>
            {option}
          </option>
        ))}
      </select>
      {help && <span className="block text-xs text-gray-400 mt-1">{help}</span>}
    </label>
  )
}

export const ModalFichaMedica = ({ open, initialFicha, onCancel, onSave, saving, title }) => {
  const [loading, setLoading] = useState(true)
  const [ficha, setFicha] = useState({
    id: "" || initialFicha?.ficha_medica_id,
    deportista_id: "" || initialFicha?.deportista_id,
    grupo_sanguineo: "",
    contacto_emergencia: "",
    telefono_emergencia: "",
    parentesco_contacto: "",
    alergias: "",
    peso: "",
    altura: "",
    observaciones_medicas: "",
    enfermedades_cronicas: "",
    lesiones_y_cirugias: "",
    biceps_cm: 0,
    triceps_cm: 0,
    pecho_cm: 0,
    espalda_cm: 0,
    cintura_cm: 0,
    cadera_cm: 0,
    cuadriceps_cm: 0,
    pantorrilla_cm: 0,
    cedula_scan_url: "",
  })

  const bloodTypeOptions = ["O+", "O-", "A+", "A-", "B+", "B-", "AB+", "AB-"]

  useEffect(() => {
    if (!open) return
    const loadData = async () => {
      setLoading(true)
      try {
        if (!initialFicha?.ficha_medica_id) {
          setFicha((prev) => ({
            ...prev,
            deportista_id: initialFicha?.deportista_id,
          }))
          setLoading(false)
          return
        }

        const fichaData = await getFichaMedica(initialFicha.ficha_medica_id)
        setFicha(fichaData)
      } catch (error) {
        console.error("Error cargando ficha médica:", error)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [open, initialFicha?.ficha_medica_id, initialFicha?.deportista_id])

  const handleSubmit = (e) => {
    e.preventDefault()
    const fichaToSave = {
      ...ficha,
      peso: ficha.peso ? ficha.peso : null,
      altura: ficha.altura ? ficha.altura : null,
      biceps_cm: ficha.biceps_cm || 0,
      triceps_cm: ficha.triceps_cm || 0,
      pecho_cm: ficha.pecho_cm || 0,
      espalda_cm: ficha.espalda_cm || 0,
      cintura_cm: ficha.cintura_cm || 0,
      cadera_cm: ficha.cadera_cm || 0,
      cuadriceps_cm: ficha.cuadriceps_cm || 0,
      pantorrilla_cm: ficha.pantorrilla_cm || 0,
      cedula_scan_url: ficha.cedula_scan_url || "",
    }
    onSave(fichaToSave)
  }

  const handleClose = () => {
    setFicha({
      id: "",
      deportista_id: "",
      grupo_sanguineo: "",
      contacto_emergencia: "",
      telefono_emergencia: "",
      alergias: "",
      peso: "",
      altura: "",
      observaciones_medicas: "",
      parentesco_contacto: "",
      enfermedades_cronicas: "",
      lesiones_y_cirugias: "",
      biceps_cm: 0,
      triceps_cm: 0,
      pecho_cm: 0,
      espalda_cm: 0,
      cintura_cm: 0,
      cadera_cm: 0,
      cuadriceps_cm: 0,
      pantorrilla_cm: 0,
      cedula_scan_url: "",
    })
    onCancel()
  }

const handleExportPdf = () => {
  try {
    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 18;
    let y = 32;

    // ===== Datos del deportista desde initialFicha =====
    const deportistaNombreCompleto =
      initialFicha?.nombre_completo ||
      `${initialFicha?.nombres || ""} ${initialFicha?.apellidos || ""}`.trim();

    const cedula = initialFicha?.cedula || "";
    const grupo = initialFicha?.grupo || "";
    const posicion = initialFicha?.posicion || "";
    const edad = initialFicha?.edad ?? null;

    // ============ HEADER ============
    doc.setFillColor(37, 99, 235); // azul
    doc.rect(0, 0, pageWidth, 26, "F");

    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(17);
    doc.text("Ficha médica del deportista", pageWidth / 2, 11, {
      align: "center",
    });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    if (deportistaNombreCompleto) {
      doc.text(
        `Deportista: ${deportistaNombreCompleto}`,
        pageWidth / 2,
        18,
        { align: "center" }
      );
    }
    if (cedula) {
      doc.text(`Cédula: ${cedula}`, pageWidth / 2, 23, { align: "center" });
    }

    // Volvemos a negro para el contenido
    doc.setTextColor(0, 0, 0);

    // ============ HELPERS ============
    const ensurePageSpace = (needed) => {
      const space = needed || 16;
      if (y + space > pageHeight - 15) {
        doc.addPage();
        y = 20;
      }
    };

    const sectionTitle = (title) => {
      ensurePageSpace(14);
      doc.setFillColor(239, 246, 255); // azul muy claro
      doc.setDrawColor(191, 219, 254); // borde azul claro
      doc.roundedRect(
        marginX - 2,
        y - 6,
        pageWidth - marginX * 2 + 4,
        9,
        2,
        2,
        "FD"
      );

      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(37, 99, 235);
      doc.text(title, marginX, y);

      y += 10;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
    };

    // Campo UNA COLUMNA
    const addField = (label, value) => {
      if (value === undefined || value === null || value === "") return;

      const fullText = `${label}: ${value}`;
      const maxWidth = pageWidth - marginX * 2;
      const lines = doc.splitTextToSize(fullText, maxWidth);
      const neededHeight = lines.length * 6 + 4;

      ensurePageSpace(neededHeight);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.setTextColor(15, 23, 42);

      doc.text(lines, marginX, y);
      y += neededHeight;
    };

    // 🔹 Medidas en dos columnas, pero cada fila alineada
    const addMeasureRow = (leftLabel, leftValue, rightLabel, rightValue) => {
      const leftText = `${leftLabel}: ${leftValue ?? 0}`;
      const rightText = `${rightLabel}: ${rightValue ?? 0}`;

      const colWidth = (pageWidth - marginX * 2) / 2;
      const leftX = marginX;
      const rightX = marginX + colWidth + 5; // pequeño gap

      // asumimos que son valores cortos → no partimos en varias líneas
      const neededHeight = 7;
      ensurePageSpace(neededHeight);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.setTextColor(15, 23, 42);

      doc.text(leftText, leftX, y);
      doc.text(rightText, rightX, y);

      y += neededHeight;
    };

    // ============ SECCIONES ============

    // 1) Datos del deportista
    sectionTitle("Datos del deportista");
    addField("Nombre completo", deportistaNombreCompleto || "—");
    addField("Cédula", cedula || "—");
    addField("Grupo", grupo || "—");
    addField("Posición", posicion || "—");
    if (edad != null) addField("Edad", `${edad} años`);

    // 2) Información básica
    sectionTitle("Información básica");
    addField("Grupo sanguíneo", ficha.grupo_sanguineo || "—");
    addField("Peso (kg)", ficha.peso || "—");
    addField("Altura (m)", ficha.altura || "—");

    // 3) Contacto de emergencia
    sectionTitle("Contacto de emergencia");
    addField("Nombre del contacto", ficha.contacto_emergencia || "—");
    addField("Parentesco", ficha.parentesco_contacto || "—");
    addField("Teléfono", ficha.telefono_emergencia || "—");

    // 4) Información médica
    sectionTitle("Información médica");
    addField("Alergias", ficha.alergias || "—");
    addField("Enfermedades crónicas", ficha.enfermedades_cronicas || "—");
    addField("Lesiones y cirugías", ficha.lesiones_y_cirugias || "—");
    addField("Observaciones médicas", ficha.observaciones_medicas || "—");

    // 5) Medidas corporales (dos columnas, pero cada fila bien)
    sectionTitle("Medidas corporales (cm)");
    addMeasureRow("Bíceps", ficha.biceps_cm, "Tríceps", ficha.triceps_cm);
    addMeasureRow("Pecho", ficha.pecho_cm, "Espalda", ficha.espalda_cm);
    addMeasureRow("Cintura", ficha.cintura_cm, "Cadera", ficha.cadera_cm);
    addMeasureRow("Cuádriceps", ficha.cuadriceps_cm, "Pantorrilla", ficha.pantorrilla_cm);

    // ============ FOOTER ============
    const fecha = new Date();
    const fechaStr = fecha.toLocaleDateString("es-EC", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

    doc.setFontSize(9);
    doc.setFont("helvetica", "italic");
    doc.setTextColor(100, 116, 139);
    doc.text(
      `Generado el ${fechaStr}`,
      pageWidth - marginX,
      pageHeight - 10,
      { align: "right" }
    );

    doc.save(
      `ficha-medica-${deportistaNombreCompleto || ficha.deportista_id || "deportista"}.pdf`
    );
  } catch (error) {
    console.error("Error exportando ficha a PDF:", error);
    Swal.fire("Error", "No se pudo exportar la ficha a PDF", "error");
  }
};


  return (
    <Modal open={open} onCancel={handleClose} title={title || "Ficha médica"} footer={null} width={900}>
      {loading ? (
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="space-y-1">
                <div className="h-3 w-20 bg-slate-200 rounded animate-pulse" />
                <div className="h-9 w-full bg-slate-100 rounded-xl animate-pulse" />
              </div>
            ))}
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={`textarea-${i}`} className="sm:col-span-2 md:col-span-3 space-y-1">
                <div className="h-3 w-24 bg-slate-200 rounded animate-pulse" />
                <div className="h-20 w-full bg-slate-100 rounded-xl animate-pulse" />
              </div>
            ))}
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <div className="h-9 w-20 bg-slate-100 rounded-xl animate-pulse" />
            <div className="h-9 w-32 bg-slate-200 rounded-xl animate-pulse" />
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Información Básica */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-slate-700">Información Básica</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              <SelectField
                label="Grupo Sanguíneo"
                value={ficha.grupo_sanguineo}
                onChange={(v) => setFicha((f) => ({ ...f, grupo_sanguineo: v }))}
                options={bloodTypeOptions}
                placeholder="Selecciona o escribe"
                disabled={saving}
              />
              <Field
                label="Peso (kg)"
                type="number"
                step="0.01"
                value={ficha.peso}
                onChange={(v) => setFicha((f) => ({ ...f, peso: v }))}
                placeholder="Ej: 70.5"
                disabled={saving}
              />
              <Field
                label="Altura (m)"
                type="number"
                step="0.01"
                value={ficha.altura}
                onChange={(v) => setFicha((f) => ({ ...f, altura: v }))}
                placeholder="Ej: 1.75"
                disabled={saving}
              />
            </div>
          </div>

          {/* Contacto de Emergencia */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-slate-700">Contacto de Emergencia</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              <Field
                label="Nombre del Contacto"
                value={ficha.contacto_emergencia}
                onChange={(v) => setFicha((f) => ({ ...f, contacto_emergencia: v }))}
                placeholder="Nombre del contacto"
                disabled={saving}
              />
              <Field
                label="Parentesco"
                value={ficha.parentesco_contacto}
                onChange={(v) => setFicha((f) => ({ ...f, parentesco_contacto: v }))}
                placeholder="Ej: Padre, Madre, Hermano"
                disabled={saving}
              />
              <Field
                label="Teléfono"
                type="tel"
                value={ficha.telefono_emergencia}
                onChange={(v) => setFicha((f) => ({ ...f, telefono_emergencia: v }))}
                placeholder="Número de teléfono"
                disabled={saving}
              />
            </div>
          </div>

          {/* Información Médica */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-slate-700">Información Médica</h3>
            <Field
              label="Alergias"
              value={ficha.alergias}
              onChange={(v) => setFicha((f) => ({ ...f, alergias: v }))}
              placeholder="Describe las alergias"
              disabled={saving}
            />
            <TextAreaField
              label="Enfermedades Crónicas"
              value={ficha.enfermedades_cronicas}
              onChange={(v) => setFicha((f) => ({ ...f, enfermedades_cronicas: v }))}
              placeholder="Describe las enfermedades crónicas conocidas"
              disabled={saving}
              rows={2}
            />
            <TextAreaField
              label="Lesiones y Cirugías"
              value={ficha.lesiones_y_cirugias}
              onChange={(v) => setFicha((f) => ({ ...f, lesiones_y_cirugias: v }))}
              placeholder="Describe lesiones previas o intervenciones quirúrgicas"
              disabled={saving}
              rows={2}
            />
            <TextAreaField
              label="Observaciones Médicas"
              value={ficha.observaciones_medicas}
              onChange={(v) => setFicha((f) => ({ ...f, observaciones_medicas: v }))}
              placeholder="Otras observaciones médicas relevantes"
              disabled={saving}
              rows={2}
            />
          </div>

          {/* Medidas Corporales */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-slate-700">Medidas Corporales (cm)</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
              <Field
                label="Bíceps"
                type="number"
                step="0.1"
                value={ficha.biceps_cm}
                onChange={(v) => setFicha((f) => ({ ...f, biceps_cm: v }))}
                placeholder="cm"
                disabled={saving}
              />
              <Field
                label="Tríceps"
                type="number"
                step="0.1"
                value={ficha.triceps_cm}
                onChange={(v) => setFicha((f) => ({ ...f, triceps_cm: v }))}
                placeholder="cm"
                disabled={saving}
              />
              <Field
                label="Pecho"
                type="number"
                step="0.1"
                value={ficha.pecho_cm}
                onChange={(v) => setFicha((f) => ({ ...f, pecho_cm: v }))}
                placeholder="cm"
                disabled={saving}
              />
              <Field
                label="Espalda"
                type="number"
                step="0.1"
                value={ficha.espalda_cm}
                onChange={(v) => setFicha((f) => ({ ...f, espalda_cm: v }))}
                placeholder="cm"
                disabled={saving}
              />
              <Field
                label="Cintura"
                type="number"
                step="0.1"
                value={ficha.cintura_cm}
                onChange={(v) => setFicha((f) => ({ ...f, cintura_cm: v }))}
                placeholder="cm"
                disabled={saving}
              />
              <Field
                label="Cadera"
                type="number"
                step="0.1"
                value={ficha.cadera_cm}
                onChange={(v) => setFicha((f) => ({ ...f, cadera_cm: v }))}
                placeholder="cm"
                disabled={saving}
              />
              <Field
                label="Cuádriceps"
                type="number"
                step="0.1"
                value={ficha.cuadriceps_cm}
                onChange={(v) => setFicha((f) => ({ ...f, cuadriceps_cm: v }))}
                placeholder="cm"
                disabled={saving}
              />
              <Field
                label="Pantorrilla"
                type="number"
                step="0.1"
                value={ficha.pantorrilla_cm}
                onChange={(v) => setFicha((f) => ({ ...f, pantorrilla_cm: v }))}
                placeholder="cm"
                disabled={saving}
              />
            </div>
          </div>

          {/* Buttons */}
          <div className="flex items-center justify-end gap-2 pt-4 border-t">
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="rounded-xl border border-slate-300 px-4 py-2 text-sm hover:bg-slate-100 transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Cancelar
            </button>

            <button
              type="button"
              onClick={handleExportPdf}
              disabled={saving}
              className="rounded-xl bg-emerald-600 text-white px-4 py-2 text-sm hover:bg-emerald-700 disabled:opacity-60 transition disabled:cursor-not-allowed"
            >
              Exportar PDF
            </button>

            <button
              type="submit"
              disabled={saving}
              className="rounded-xl bg-blue-600 text-white px-4 py-2 text-sm hover:bg-blue-700 disabled:opacity-60 transition disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving && (
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              )}
              {saving ? "Guardando..." : "Guardar ficha"}
            </button>
          </div>
        </form>
      )}
    </Modal>
  )
}
